import React, { useState } from "react";
import {
  X,
  Search,
  CheckCircle,
  AlertCircle,
  Loader,
  Save,
  RefreshCw,
  Globe,
  MapPin,
  Tag,
  FileText,
  Image as ImageIcon,
  MessageSquare,
  Zap,
  Bot,
  Bug,
  Key,
  User,
} from "lucide-react";
import {
  SEOOptimizationService,
  SEOOptimizationResult,
} from "../services/seoOptimizationService";
import { SitemapService } from "../services/sitemapService";
import { isOpenAIAvailable } from "../config/runtime-config";

interface SEOOptimizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
  currentExcerpt: string;
  currentMetaDescription: string;
  postId?: string;
  seoOptimizedAt?: string | null;
  seoOptimizationVersion?: string | null;
  onOptimizationComplete?: (result: SEOOptimizationResult) => void;
}

export default function SEOOptimizationModal({
  isOpen,
  onClose,
  title,
  content,
  currentExcerpt,
  currentMetaDescription,
  postId,
  seoOptimizedAt,
  seoOptimizationVersion,
  onOptimizationComplete,
}: SEOOptimizationModalProps) {
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationResult, setOptimizationResult] =
    useState<SEOOptimizationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [sitemapStatus, setSitemapStatus] = useState<{
    needsRegeneration: boolean;
    reason: string;
    lastUpdate: string | null;
  } | null>(null);
  const [showApiKeyManager, setShowApiKeyManager] = useState(false);

  const handleOptimize = async () => {
    try {
      setIsOptimizing(true);
      setError(null);

      console.log("🚀 Starting SEO optimization...");

      const result = await SEOOptimizationService.optimizeBlogPost(
        title,
        content,
        currentExcerpt,
        currentMetaDescription,
      );

      console.log("✅ SEO optimization completed successfully");
      setOptimizationResult(result);

      if (postId) {
        console.log("💾 Saving optimization results to database...");
        try {
          await SEOOptimizationService.updateBlogPostSEO(postId, result);
        } catch (dbError) {
          console.error("Database update failed:", dbError);
          // Don't fail the entire optimization if database update fails
        }
      }

      if (onOptimizationComplete) {
        onOptimizationComplete(result);
      }
    } catch (err) {
      console.error("SEO optimization failed:", err);

      // Provide specific error messages for common issues
      let errorMessage = "Failed to optimize SEO";
      if (err instanceof Error) {
        if (err.message.includes("OpenAI API key not configured")) {
          errorMessage =
            "OpenAI API key not configured. Please contact your administrator to set up the VITE_OPENAI_API_KEY environment variable.";
        } else if (err.message.includes("401")) {
          errorMessage =
            "Invalid OpenAI API key. Please check your API key configuration.";
        } else if (
          err.message.includes("429") ||
          err.message.includes("Rate limit exceeded")
        ) {
          errorMessage =
            "Rate limit exceeded. Please wait 1-2 minutes and try again, or check your OpenAI account usage.";
        } else if (err.message.includes("500")) {
          errorMessage =
            "OpenAI server error. Please try again in a few minutes.";
        } else {
          errorMessage = err.message;
        }
      }
      setError(errorMessage);
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleUpdateSitemap = async () => {
    try {
      const result = await SitemapService.triggerSitemapRegeneration();
      if (result.success) {
        console.log("✅ Sitemap regeneration triggered successfully");
        console.log("📋 Instructions:", result.instructions?.join("\n"));
      } else {
        setError("Failed to update sitemap");
      }
    } catch (err) {
      setError("Failed to update sitemap");
    }
  };

  const handleApplyOptimization = () => {
    console.log("🔧 Apply Optimization button clicked");
    console.log("📊 Optimization result:", optimizationResult);
    console.log("📞 onOptimizationComplete callback:", onOptimizationComplete);

    if (optimizationResult && onOptimizationComplete) {
      console.log("✅ Applying optimization...");
      onOptimizationComplete(optimizationResult);
      console.log("✅ Optimization applied, closing modal");
      onClose();
    } else {
      console.error("❌ Cannot apply optimization:", {
        hasOptimizationResult: !!optimizationResult,
        hasCallback: !!onOptimizationComplete,
      });
    }
  };

  const handleDebugEnvironment = () => {
    console.log("🔍 Debugging Environment Variables...");

    // Check all possible sources for the API key
    const envKey = import.meta.env.VITE_OPENAI_API_KEY;
    const localStorageKey = localStorage.getItem("openai_api_key");
    const isAvailable = isOpenAIAvailable();

    console.log(
      "Environment Variable:",
      envKey ? `${envKey.substring(0, 20)}...` : "NOT SET",
    );
    console.log(
      "localStorage Key:",
      localStorageKey ? `${localStorageKey.substring(0, 20)}...` : "NOT SET",
    );
    console.log("Is OpenAI Available:", isAvailable);
    console.log("Current URL:", window.location.href);
    console.log("Hostname:", window.location.hostname);
    console.log("Build Mode:", import.meta.env.MODE);

    // Show comprehensive debug info
    const debugInfo = `Environment Debug:
URL: ${window.location.href}
Environment Variable: ${envKey ? "SET" : "NOT SET"}
localStorage Key: ${localStorageKey ? "SET" : "NOT SET"}
Is OpenAI Available: ${isAvailable ? "YES" : "NO"}
Build Mode: ${import.meta.env.MODE}
Hostname: ${window.location.hostname}

If OpenAI is not available, you can manually enter your API key below:`;

    const manualKey = prompt(
      debugInfo + "\n\nEnter your OpenAI API key (or leave empty to skip):",
    );

    if (manualKey && manualKey.trim()) {
      // Store the key in localStorage for this session
      localStorage.setItem("openai_api_key", manualKey.trim());
      console.log("🔑 Manual API key stored in localStorage");
      alert("API key stored! Try the SEO optimization again.");
    }
  };

  if (!isOpen) return null;

  // Defensive programming to prevent crashes
  const safeSeoOptimizedAt = seoOptimizedAt || null;
  const safeSeoOptimizationVersion = seoOptimizationVersion || null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Search className="h-6 w-6 text-[#F98B3D] mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                AI SEO Optimization
              </h2>
              {safeSeoOptimizedAt && (
                <div className="flex items-center mt-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  <span className="text-sm text-green-600">
                    Last optimized:{" "}
                    {new Date(safeSeoOptimizedAt).toLocaleDateString()}
                    {safeSeoOptimizationVersion &&
                      ` (v${safeSeoOptimizationVersion})`}
                  </span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {!optimizationResult ? (
            /* Initial State */
            <div className="p-6">
              <div className="text-center">
                <div className="mb-6">
                  <Globe className="h-16 w-16 text-[#F98B3D] mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Optimize Your Blog Post for Search Engines
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Our AI will analyze your content and optimize it for maximum
                    search engine visibility, including meta descriptions,
                    keywords, local SEO, schema markup, and more.
                  </p>
                </div>

                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-center">
                      <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                      <span className="text-red-700">{error}</span>
                    </div>
                    {error.includes("Rate limit exceeded") && (
                      <div className="mt-3">
                        <button
                          onClick={handleOptimize}
                          disabled={isOptimizing}
                          className="inline-flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                          {isOptimizing ? (
                            <>
                              <Loader className="h-3 w-3 mr-1 animate-spin" />
                              Retrying...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-3 w-3 mr-1" />
                              Retry Now
                            </>
                          )}
                        </button>
                        <p className="text-xs text-gray-600 mt-1">
                          Rate limits reset every minute. You can also check
                          your OpenAI account usage.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-col space-y-3">
                  <div className="flex flex-col space-y-3">
                    <button
                      onClick={handleOptimize}
                      disabled={isOptimizing || !isOpenAIAvailable()}
                      className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] disabled:opacity-50"
                    >
                      {isOptimizing ? (
                        <>
                          <Loader className="h-5 w-5 mr-2 animate-spin" />
                          Optimizing...
                        </>
                      ) : (
                        <>
                          <Zap className="h-5 w-5 mr-2" />
                          Start SEO Optimization
                        </>
                      )}
                    </button>

                    {!isOpenAIAvailable() && (
                      <button
                        onClick={() => setShowApiKeyManager(true)}
                        className="inline-flex items-center px-4 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100"
                      >
                        <Key className="h-4 w-4 mr-2" />
                        Set API Key
                      </button>
                    )}

                    <button
                      onClick={handleDebugEnvironment}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <Bug className="h-4 w-4 mr-2" />
                      Debug Environment
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Results State */
            <div className="flex h-full">
              {/* Sidebar */}
              <div className="w-64 bg-gray-50 border-r border-gray-200 p-4">
                <nav className="space-y-2">
                  {[
                    { id: "overview", label: "Overview", icon: Globe },
                    { id: "meta", label: "Meta Tags", icon: Tag },
                    { id: "content", label: "Content", icon: FileText },
                    { id: "local", label: "Local SEO", icon: MapPin },
                    { id: "aeo", label: "AEO", icon: Search },
                    {
                      id: "accessibility",
                      label: "Accessibility",
                      icon: CheckCircle,
                    },
                    { id: "ai-search", label: "AI Search", icon: Bot },
                    { id: "technical", label: "Technical", icon: Zap },
                    { id: "schema", label: "Schema", icon: MessageSquare },
                    { id: "faqs", label: "FAQs", icon: MessageSquare },
                    { id: "images", label: "Images", icon: ImageIcon },
                    { id: "author", label: "Author Schema", icon: User },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setActiveTab(tab.id);
                      }}
                      className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        activeTab === tab.id
                          ? "bg-[#F98B3D] text-white"
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                      }`}
                    >
                      <tab.icon className="h-4 w-4 mr-2" />
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Main Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {activeTab === "overview" && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <h4 className="font-medium text-green-900 mb-2">
                          Meta Title
                        </h4>
                        <p className="text-sm text-green-700">
                          {optimizationResult.metaTitle}
                        </p>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <h4 className="font-medium text-blue-900 mb-2">
                          Meta Description
                        </h4>
                        <p className="text-sm text-blue-700">
                          {optimizationResult.metaDescription}
                        </p>
                      </div>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                      <h4 className="font-medium text-yellow-900 mb-2">
                        Keywords
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {optimizationResult.metaKeywords.map(
                          (keyword, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded"
                            >
                              {keyword}
                            </span>
                          ),
                        )}
                      </div>
                    </div>

                    {/* Advanced SEO Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                        <h4 className="font-medium text-purple-900 mb-2">
                          AEO Features
                        </h4>
                        <p className="text-sm text-purple-700">
                          {optimizationResult.aeoOptimization?.answerBoxes
                            ?.length || 0}{" "}
                          Answer Boxes
                        </p>
                        <p className="text-sm text-purple-700">
                          {optimizationResult.aeoOptimization
                            ?.voiceSearchQueries?.length || 0}{" "}
                          Voice Queries
                        </p>
                      </div>
                      <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                        <h4 className="font-medium text-indigo-900 mb-2">
                          Accessibility
                        </h4>
                        <p className="text-sm text-indigo-700">
                          {optimizationResult.accessibilityOptimization
                            ?.altTextSuggestions?.length || 0}{" "}
                          Alt Text Suggestions
                        </p>
                        <p className="text-sm text-indigo-700">
                          {optimizationResult.accessibilityOptimization
                            ?.headingStructure?.length || 0}{" "}
                          Heading Improvements
                        </p>
                      </div>
                      <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                        <h4 className="font-medium text-teal-900 mb-2">
                          AI Search
                        </h4>
                        <p className="text-sm text-teal-700">
                          {optimizationResult.aiSearchOptimization
                            ?.semanticKeywords?.length || 0}{" "}
                          Semantic Keywords
                        </p>
                        <p className="text-sm text-teal-700">
                          {optimizationResult.aiSearchOptimization
                            ?.topicClusters?.length || 0}{" "}
                          Topic Clusters
                        </p>
                      </div>
                      <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                        <h4 className="font-medium text-orange-900 mb-2">
                          Technical SEO
                        </h4>
                        <p className="text-sm text-orange-700">
                          {optimizationResult.technicalSEO?.structuredDataTypes
                            ?.length || 0}{" "}
                          Schema Types
                        </p>
                        <p className="text-sm text-orange-700">
                          {optimizationResult.technicalSEO
                            ?.internalLinkingSuggestions?.length || 0}{" "}
                          Link Suggestions
                        </p>
                      </div>
                    </div>

                    <div className="flex space-x-4">
                      <button
                        onClick={(e) => {
                          console.log("🔘 Apply Optimization button clicked!");
                          e.preventDefault();
                          e.stopPropagation();
                          handleApplyOptimization();
                        }}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
                        disabled={!optimizationResult}
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Apply Optimization{" "}
                        {!optimizationResult && "(No Results)"}
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleUpdateSitemap();
                        }}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Update Sitemap
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === "meta" && (
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Meta Title
                      </h4>
                      <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded border">
                        {optimizationResult.metaTitle}
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Meta Description
                      </h4>
                      <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded border">
                        {optimizationResult.metaDescription}
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Excerpt
                      </h4>
                      <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded border">
                        {optimizationResult.excerpt}
                      </p>
                    </div>
                  </div>
                )}

                {activeTab === "content" && (
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Content Optimization Suggestions
                      </h4>
                      <ul className="list-disc list-inside space-y-2 text-sm text-gray-600">
                        {optimizationResult.contentOptimization.suggestions.map(
                          (suggestion, index) => (
                            <li key={index}>{suggestion}</li>
                          ),
                        )}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Improved Content
                      </h4>
                      <div className="bg-gray-50 p-4 rounded border max-h-64 overflow-y-auto">
                        <pre className="text-sm text-gray-600 whitespace-pre-wrap">
                          {
                            optimizationResult.contentOptimization
                              .improvedContent
                          }
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "local" && (
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Location
                      </h4>
                      <p className="text-sm text-gray-600">
                        {optimizationResult.localSEO.location}
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Service Areas
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {optimizationResult.localSEO.serviceArea.map(
                          (area, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded"
                            >
                              {area}
                            </span>
                          ),
                        )}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Local Keywords
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {optimizationResult.localSEO.localKeywords.map(
                          (keyword, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded"
                            >
                              {keyword}
                            </span>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "schema" && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">
                      Schema Markup
                    </h4>
                    <div className="bg-gray-50 p-4 rounded border max-h-64 overflow-y-auto">
                      <pre className="text-sm text-gray-600">
                        {JSON.stringify(
                          optimizationResult.schemaMarkup,
                          null,
                          2,
                        )}
                      </pre>
                    </div>
                  </div>
                )}

                {activeTab === "faqs" && (
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 mb-2">
                      Generated FAQs
                    </h4>
                    {optimizationResult.faqs.map((faq, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <h5 className="font-medium text-gray-900 mb-2">
                          {faq.question}
                        </h5>
                        <p className="text-sm text-gray-600">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === "aeo" && (
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Answer Boxes for Featured Snippets
                      </h4>
                      {optimizationResult.aeoOptimization?.answerBoxes?.map(
                        (box, index) => (
                          <div
                            key={index}
                            className="border border-gray-200 rounded-lg p-4 mb-4"
                          >
                            <h5 className="font-medium text-gray-900 mb-2">
                              {box.question}
                            </h5>
                            <p className="text-sm text-gray-600 mb-2">
                              {box.answer}
                            </p>
                            <span className="text-xs text-gray-500">
                              Confidence: {box.confidence}%
                            </span>
                          </div>
                        ),
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Voice Search Queries
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {optimizationResult.aeoOptimization?.voiceSearchQueries?.map(
                          (query, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded"
                            >
                              {query}
                            </span>
                          ),
                        )}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Conversational Keywords
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {optimizationResult.aeoOptimization?.conversationalKeywords?.map(
                          (keyword, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded"
                            >
                              {keyword}
                            </span>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "accessibility" && (
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Alt Text Suggestions
                      </h4>
                      <ul className="list-disc list-inside space-y-2 text-sm text-gray-600">
                        {optimizationResult.accessibilityOptimization?.altTextSuggestions?.map(
                          (suggestion, index) => (
                            <li key={index}>{suggestion}</li>
                          ),
                        )}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Heading Structure
                      </h4>
                      {optimizationResult.accessibilityOptimization?.headingStructure?.map(
                        (heading, index) => (
                          <div
                            key={index}
                            className="border border-gray-200 rounded-lg p-3 mb-2"
                          >
                            <span className="text-xs text-gray-500">
                              H{heading.level}
                            </span>
                            <p className="text-sm text-gray-600 mt-1">
                              {heading.suggestedText}
                            </p>
                          </div>
                        ),
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        ARIA Labels
                      </h4>
                      {optimizationResult.accessibilityOptimization?.ariaLabels?.map(
                        (aria, index) => (
                          <div
                            key={index}
                            className="border border-gray-200 rounded-lg p-3 mb-2"
                          >
                            <span className="text-xs text-gray-500">
                              {aria.element}
                            </span>
                            <p className="text-sm text-gray-600 mt-1">
                              {aria.label}
                            </p>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}

                {activeTab === "ai-search" && (
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Semantic Keywords
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {optimizationResult.aiSearchOptimization?.semanticKeywords?.map(
                          (keyword, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded"
                            >
                              {keyword}
                            </span>
                          ),
                        )}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Entity Mentions
                      </h4>
                      {optimizationResult.aiSearchOptimization?.entityMentions?.map(
                        (entity, index) => (
                          <div
                            key={index}
                            className="border border-gray-200 rounded-lg p-3 mb-2"
                          >
                            <span className="text-xs text-gray-500">
                              {entity.type}
                            </span>
                            <p className="text-sm font-medium text-gray-900">
                              {entity.entity}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                              {entity.context}
                            </p>
                          </div>
                        ),
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Topic Clusters
                      </h4>
                      {optimizationResult.aiSearchOptimization?.topicClusters?.map(
                        (cluster, index) => (
                          <div
                            key={index}
                            className="border border-gray-200 rounded-lg p-4 mb-4"
                          >
                            <h5 className="font-medium text-gray-900 mb-2">
                              {cluster.mainTopic}
                            </h5>
                            <div className="mb-2">
                              <span className="text-xs text-gray-500">
                                Subtopics:
                              </span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {cluster.subtopics.map((subtopic, subIndex) => (
                                  <span
                                    key={subIndex}
                                    className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                                  >
                                    {subtopic}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div>
                              <span className="text-xs text-gray-500">
                                Related Keywords:
                              </span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {cluster.relatedKeywords.map(
                                  (keyword, keyIndex) => (
                                    <span
                                      key={keyIndex}
                                      className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded"
                                    >
                                      {keyword}
                                    </span>
                                  ),
                                )}
                              </div>
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}

                {activeTab === "technical" && (
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Structured Data Types
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {optimizationResult.technicalSEO?.structuredDataTypes?.map(
                          (type, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded"
                            >
                              {type}
                            </span>
                          ),
                        )}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Internal Linking Suggestions
                      </h4>
                      {optimizationResult.technicalSEO?.internalLinkingSuggestions?.map(
                        (link, index) => (
                          <div
                            key={index}
                            className="border border-gray-200 rounded-lg p-3 mb-2"
                          >
                            <p className="text-sm font-medium text-gray-900">
                              {link.anchorText}
                            </p>
                            <p className="text-xs text-gray-500">
                              {link.targetUrl}
                            </p>
                            <span className="text-xs text-gray-600">
                              {link.relevance}
                            </span>
                          </div>
                        ),
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Performance Optimization
                      </h4>
                      <ul className="list-disc list-inside space-y-2 text-sm text-gray-600">
                        {optimizationResult.technicalSEO?.performanceOptimization?.suggestions?.map(
                          (suggestion, index) => (
                            <li key={index}>{suggestion}</li>
                          ),
                        )}
                      </ul>
                    </div>
                  </div>
                )}

                {activeTab === "images" && (
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        Featured Image Suggestion
                      </h4>
                      <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded border">
                        {optimizationResult.featuredImage}
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        CTA Suggestion
                      </h4>
                      <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded border">
                        {optimizationResult.cta}
                      </p>
                    </div>
                  </div>
                )}

                {activeTab === "author" && (
                  <div className="space-y-6">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <h4 className="font-medium text-blue-900 mb-2">
                        Author Information
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <span className="font-medium text-blue-800">
                            Name:
                          </span>
                          <span className="ml-2 text-blue-700">
                            {optimizationResult.authorSchema?.name}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-blue-800">
                            Job Title:
                          </span>
                          <span className="ml-2 text-blue-700">
                            {optimizationResult.authorSchema?.jobTitle}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-blue-800">
                            Bio:
                          </span>
                          <p className="mt-1 text-sm text-blue-700">
                            {optimizationResult.authorSchema?.bio}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <h4 className="font-medium text-green-900 mb-2">
                        Experience & Credentials
                      </h4>
                      <div className="space-y-2">
                        <div>
                          <span className="font-medium text-green-800">
                            AI Experience:
                          </span>
                          <span className="ml-2 text-green-700">
                            {
                              optimizationResult.authorSchema?.yearsExperience
                                ?.ai
                            }{" "}
                            years
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-green-800">
                            Project Management:
                          </span>
                          <span className="ml-2 text-green-700">
                            {
                              optimizationResult.authorSchema?.yearsExperience
                                ?.projectManagement
                            }{" "}
                            years
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-green-800">
                            Software Development:
                          </span>
                          <span className="ml-2 text-green-700">
                            {
                              optimizationResult.authorSchema?.yearsExperience
                                ?.softwareDevelopment
                            }{" "}
                            years
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-green-800">
                            Education:
                          </span>
                          <span className="ml-2 text-green-700">
                            {optimizationResult.authorSchema?.education}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                      <h4 className="font-medium text-purple-900 mb-2">
                        Expertise Areas
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {optimizationResult.authorSchema?.expertise?.map(
                          (expertise, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded"
                            >
                              {expertise}
                            </span>
                          ),
                        )}
                      </div>
                    </div>

                    <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                      <h4 className="font-medium text-orange-900 mb-2">
                        Contact & Social Links
                      </h4>
                      <div className="space-y-2">
                        <div>
                          <span className="font-medium text-orange-800">
                            Email:
                          </span>
                          <span className="ml-2 text-orange-700">
                            {optimizationResult.authorSchema?.email}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-orange-800">
                            Phone:
                          </span>
                          <span className="ml-2 text-orange-700">
                            {optimizationResult.authorSchema?.phone}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-orange-800">
                            LinkedIn:
                          </span>
                          <a
                            href={optimizationResult.authorSchema?.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 text-orange-700 hover:text-orange-800 underline"
                          >
                            {optimizationResult.authorSchema?.linkedin}
                          </a>
                        </div>
                        <div>
                          <span className="font-medium text-orange-800">
                            GitHub:
                          </span>
                          <a
                            href={optimizationResult.authorSchema?.github}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 text-orange-700 hover:text-orange-800 underline"
                          >
                            {optimizationResult.authorSchema?.github}
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <h4 className="font-medium text-gray-900 mb-2">
                        SEO Benefits
                      </h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Enhanced E-E-A-T signals for search engines</li>
                        <li>• Rich snippet potential for author information</li>
                        <li>• Improved trust signals for AI-related content</li>
                        <li>• Better authority ranking for Omaha market</li>
                        <li>• Voice search optimization for author queries</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* API Key Manager Modal */}
      {showApiKeyManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center">
                <Key className="h-5 w-5 mr-2" />
                Set OpenAI API Key
              </h2>
              <button
                onClick={() => setShowApiKeyManager(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Enter your OpenAI API key to enable SEO optimization:
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  OpenAI API Key
                </label>
                <input
                  type="password"
                  placeholder="sk-..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  id="api-key-input"
                />
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    const input = document.getElementById(
                      "api-key-input",
                    ) as HTMLInputElement;
                    const key = input?.value?.trim();
                    if (key) {
                      localStorage.setItem("openai_api_key", key);
                      alert("API key set! Try the SEO optimization again.");
                      setShowApiKeyManager(false);
                    } else {
                      alert("Please enter a valid API key");
                    }
                  }}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Set Key
                </button>
                <button
                  onClick={() => setShowApiKeyManager(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>

              <div className="text-xs text-gray-500">
                <p>• The key will be stored in your browser for this session</p>
                <p>• It will be cleared when you close the browser</p>
                <p>• This is a temporary solution for staging testing</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
